import React from "react";
import { useSelector } from "react-redux";

import Button from "@cayuse/shared-react.components.button";
import { get, isObjectLike } from "lodash";

import { isNonEmptyObject } from "@cayuse/shared-react.utils.type-checks";

import { FlowableCombobox } from "components/flowable/FlowableCombobox";
import { LongTextAccordion } from "components/LongTextAccordion";
import { useDictionary } from "hooks";
import { tenantSelectors } from "store/selectors";

import styles from "../styles.css";
import { useFundingProgramContext } from "../useFundingProgramContext";
import { RoundsBlock } from "./RoundsBlock";


export const ProgramBlock = ({ index, errors, touched, forceValidations }) => {
	const fundingSchemeDictionary = useSelector(tenantSelectors.useFundingSchemeDictionary);
	const dictionary = useDictionary(`${fundingSchemeDictionary ? "fundingScheme" : "fundingProgram"}.form`);
	const ctx = useFundingProgramContext({ programIndex: index });

	React.useEffect(() => {
		if (ctx.current.program.value) {
			ctx.loadProgramByIdAndUpdateCurrent(ctx.current.program.value);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ ctx.current.program.value ]);

	const isRoundError = error => {
		return error.includes("Round");
	};

	return (
		<div className={styles.container}>
			<div className={styles.block}>
				<FlowableCombobox
					disabled={ctx.disabled}
					label={dictionary.programsBlock.inputLabel}
					placeholder={dictionary.programsBlock.inputPlaceholder}
					required={ctx.config.isRequired}
					loadOptions={ctx.loadProgramOptions}
					onFocus={ctx.onFocus}
					onBlur={ctx.onBlur}
					onChange={ctx.onSelectProgram}
					backspaceRemovesValue={false}
					value={ctx.current.program.value ? ctx.current.program : null}
					invalid={ctx.touched && get(ctx.errors, `${index}.program`)}
					loadingMessage="Loading..."
				/>
				<table className={`ui table ${styles.table}`}>
					<thead>
						<tr>
							<th style={{ width: "40%" }}>{dictionary.programsBlock.tableColumns[0]}</th>
							<th style={{ width: 100 }}>{dictionary.programsBlock.tableColumns[1]}</th>
							<th>{dictionary.programsBlock.tableColumns[2]}</th>
						</tr>
					</thead>
					<tbody>
						{isNonEmptyObject(ctx.current.program.item)
							? (
								<tr>
									<td>{ctx.current.program.item.name}</td>
									<td>{ctx.current.program.item.active ? "Yes" : "No"}</td>
									<td>
										<LongTextAccordion
											value={ctx.current.program.item?.description}
										/>
									</td>
								</tr>
							) : (
								<tr data-empty="true">
									<td colSpan="3">{dictionary.programsBlock.emptyTable}</td>
								</tr>
							)}
					</tbody>
				</table>
			</div>

			<hr className={styles.dividingLine} />

			<div className={styles.blockActions}>
				{isNonEmptyObject(ctx.current.program.item) ? (
					<Button
						icon="plus"
						secondary
						className={styles.add}
						onClick={ctx.onAddRound}
						data-testid="add-round-btn"
						disabled={isObjectLike(ctx.current.round) || ctx.disabled || !ctx.current.program.hasRounds}
						onFocus={ctx.onFocus}
						onBlur={ctx.onBlur}
					>
						{dictionary.addRoundButton}
					</Button>
				) : null}

				{ctx.programs.length > 1 ? (
					<Button
						icon="trash"
						secondary
						className={styles.add}
						onClick={ctx.onRemoveProgram}
						data-testid="remove-program-btn"
						disabled={ctx.disabled}
						onFocus={ctx.onFocus}
						onBlur={ctx.onBlur}
					>
						{dictionary.removeFundingProgramButton}
					</Button>
				) : null}
			</div>

			<RoundsBlock programIndex={index} />

			<div className={styles.helpSection} role="alert">
				{(!forceValidations && !touched)
					? null
					: <>
						{Object.values(errors || {})
							.map((error, i) => (
								<span
									key={i}
									className={`flw__error-list__error-list-item ${
										isRoundError(error) ? styles.roundError : ""
									}`}
									data-testid="error-list-item"
								>{error}</span>
							))}
					</>
				}
			</div>
		</div>
	);
};
